/**
 * Voice messages — WhatsApp-style audio for the chat.
 *
 * Exposes:
 *  - useVoiceRecorder(): MediaRecorder wrapper that captures a blob + duration +
 *    a normalised waveform (live levels while recording, downsampled peaks on stop).
 *  - RecordingBar: the in-composer recording UI (timer, live waveform, cancel/lock/send).
 *  - AudioMessage: the speech-bubble player (waveform + play/pause + seek), themed
 *    for both "own" (primary bubble) and "other" (muted bubble) messages.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Trash2, Send, Lock, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';

// ── Types ──────────────────────────────────────────────────────────────────
export interface VoiceRecording {
  blob: Blob;
  url: string;
  duration: number; // seconds
  peaks: number[]; // normalised 0..1, ~40 samples
  mime: string;
}

const WAVE_BARS = 40;

// ── Helpers ────────────────────────────────────────────────────────────────
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Bucket-average an arbitrary level series into `n` bars, normalised to [0.12, 1]. */
function downsample(src: number[], n: number): number[] {
  if (src.length === 0) return Array.from({ length: n }, () => 0.15);
  const out: number[] = [];
  const size = src.length / n;
  for (let i = 0; i < n; i++) {
    const start = Math.floor(i * size);
    const end = Math.max(start + 1, Math.floor((i + 1) * size));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end && j < src.length; j++) {
      sum += src[j];
      count++;
    }
    out.push(count ? sum / count : 0);
  }
  const max = Math.max(...out, 0.0001);
  return out.map((v) => Math.max(0.12, v / max));
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  if (!supported) return '';
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

// ── Hook ───────────────────────────────────────────────────────────────────
export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms
  const [levels, setLevels] = useState<number[]>([]);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const levelsRef = useRef<number[]>([]);
  const cancelledRef = useRef(false);
  const resolveRef = useRef<((r: VoiceRecording | null) => void) | null>(null);

  const cleanup = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      void ctxRef.current.close();
    }
    ctxRef.current = null;
  }, []);

  const start = useCallback(async (): Promise<boolean> => {
    if (recorderRef.current) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mime = pickMimeType();
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      levelsRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const resolve = resolveRef.current;
        resolveRef.current = null;
        const chunks = chunksRef.current;
        const type = recorder.mimeType || mime || 'audio/webm';
        cleanup();
        recorderRef.current = null;
        setRecording(false);
        if (cancelledRef.current || chunks.length === 0) {
          resolve?.(null);
          return;
        }
        const blob = new Blob(chunks, { type });
        const duration = (Date.now() - startRef.current) / 1000;
        const peaks = downsample(levelsRef.current, WAVE_BARS);
        resolve?.({ blob, url: URL.createObjectURL(blob), duration, peaks, mime: blob.type });
      };

      // Live level metering via an analyser on the mic stream.
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let lastSample = 0;

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        setElapsed(now - startRef.current);
        if (now - lastSample > 90) {
          lastSample = now;
          const level = Math.min(1, rms * 3.4);
          levelsRef.current.push(level);
          setLevels((prev) => [...prev.slice(-(WAVE_BARS - 1)), level]);
        }
        rafRef.current = requestAnimationFrame(tick);
      };

      startRef.current = Date.now();
      setElapsed(0);
      setLevels([]);
      recorder.start();
      setRecording(true);
      rafRef.current = requestAnimationFrame(tick);
      return true;
    } catch (err) {
      console.error('Voice recorder: getUserMedia failed', err);
      cleanup();
      recorderRef.current = null;
      return false;
    }
  }, [cleanup]);

  const finish = useCallback((cancelled: boolean): Promise<VoiceRecording | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      cancelledRef.current = cancelled;
      resolveRef.current = resolve;
      recorder.stop();
    });
  }, []);

  const stop = useCallback(() => finish(false), [finish]);
  const cancel = useCallback(() => finish(true), [finish]);

  // Stop everything if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          cancelledRef.current = true;
          recorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      cleanup();
    };
  }, [cleanup]);

  return { recording, elapsed, levels, start, stop, cancel };
}

// ── Waveform (shared) ────────────────────────────────────────────────────────
function WaveBars({
  peaks,
  progress,
  playedClass,
  restClass,
}: {
  peaks: number[];
  progress: number; // 0..1
  playedClass: string;
  restClass: string;
}) {
  return (
    <>
      {peaks.map((p, i) => {
        const played = peaks.length > 0 && i / peaks.length < progress;
        const height = Math.max(3, Math.round(p * 26));
        return (
          <span
            key={i}
            style={{ height: `${height}px` }}
            className={cn('w-[2px] rounded-full transition-colors', played ? playedClass : restClass)}
          />
        );
      })}
    </>
  );
}

// ── Recording bar (in composer) ──────────────────────────────────────────────
export function RecordingBar({
  elapsed,
  levels,
  locked,
  cancelArmed,
  onCancel,
  onSend,
}: {
  elapsed: number; // ms
  levels: number[];
  locked: boolean;
  cancelArmed: boolean;
  onCancel: () => void;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  // Pad the live levels to a fixed bar count so the wave doesn't jump around.
  const bars =
    levels.length >= WAVE_BARS
      ? levels.slice(-WAVE_BARS)
      : [...Array<number>(WAVE_BARS - levels.length).fill(0.12), ...levels];

  return (
    <div className="flex items-center gap-2">
      {/* Cancel */}
      <button
        type="button"
        onClick={onCancel}
        aria-label={t('voiceMessage.cancelRecording', 'Cancelar gravação')}
        className={cn(
          'p-2.5 rounded-xl flex-shrink-0 transition-colors',
          cancelArmed
            ? 'bg-destructive text-white'
            : 'text-destructive hover:bg-destructive/10'
        )}
      >
        <Trash2 size={18} />
      </button>

      {/* Timer + live waveform */}
      <div className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2 rounded-2xl bg-muted">
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-500/60 animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        <span className="font-mono text-xs text-foreground tabular-nums flex-shrink-0 w-9">
          {formatDuration(elapsed / 1000)}
        </span>
        <div className="flex-1 min-w-0 flex items-center justify-end gap-[2px] h-7 overflow-hidden">
          <WaveBars
            peaks={bars}
            progress={0}
            playedClass="bg-primary"
            restClass="bg-primary/70"
          />
        </div>
        {/* Hint / lock affordance */}
        {locked ? (
          <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-primary flex-shrink-0">
            <Lock size={11} /> {t('voiceMessage.locked', 'Travado')}
          </span>
        ) : (
          <span
            className={cn(
              'flex items-center gap-1 text-[10px] font-mono flex-shrink-0 transition-colors',
              cancelArmed ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            <ArrowLeft size={11} className="animate-pulse" />
            {cancelArmed ? t('voiceMessage.releaseToCancel', 'Solte para cancelar') : t('voiceMessage.drag', 'arraste')}
          </span>
        )}
      </div>

      {/* Send */}
      <button
        type="button"
        onClick={onSend}
        aria-label={t('voiceMessage.sendAudio', 'Enviar áudio')}
        className={cn(
          'p-2.5 rounded-xl bg-primary text-primary-foreground flex-shrink-0',
          'hover:bg-primary/90 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <Send size={18} />
      </button>
    </div>
  );
}

// ── Audio message (bubble player) ────────────────────────────────────────────
export function AudioMessage({
  dataUrl,
  duration,
  peaks,
  isOwn,
}: {
  dataUrl?: string;
  duration?: number;
  peaks?: number[];
  isOwn: boolean;
}) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [current, setCurrent] = useState(0); // seconds

  const bars = peaks && peaks.length > 0 ? peaks : Array.from({ length: WAVE_BARS }, () => 0.3);
  const totalDuration = duration && duration > 0 ? duration : 0;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      void audio.play();
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const dur = audio.duration && isFinite(audio.duration) ? audio.duration : totalDuration;
    if (dur > 0) {
      audio.currentTime = fraction * dur;
      setProgress(fraction);
    }
  };

  const shownTime = playing || progress > 0 ? current : totalDuration;

  return (
    <div
      className={cn(
        'flex items-center gap-3 pl-2 pr-3.5 py-2.5 rounded-2xl w-[240px] max-w-[240px]',
        isOwn
          ? 'bg-primary text-primary-foreground rounded-br-sm'
          : 'bg-muted text-foreground rounded-bl-sm'
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t('voiceMessage.pause', 'Pausar') : t('voiceMessage.play', 'Reproduzir')}
        className={cn(
          'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
          isOwn
            ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        )}
      >
        {playing ? <Pause size={16} /> : <Play size={16} className="translate-x-[1px]" />}
      </button>

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div
          onClick={handleSeek}
          className="flex items-center gap-[2px] h-7 cursor-pointer"
          role="slider"
          aria-label={t('voiceMessage.audioProgress', 'Progresso do áudio')}
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
        >
          <WaveBars
            peaks={bars}
            progress={progress}
            playedClass={isOwn ? 'bg-primary-foreground' : 'bg-primary'}
            restClass={isOwn ? 'bg-primary-foreground/40' : 'bg-muted-foreground/40'}
          />
        </div>
        <span
          className={cn(
            'font-mono text-[10px] tabular-nums leading-none',
            isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          {formatDuration(shownTime)}
        </span>
      </div>

      <audio
        ref={audioRef}
        src={dataUrl}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
          setCurrent(0);
        }}
        onTimeUpdate={(e) => {
          const audio = e.currentTarget;
          const dur = audio.duration && isFinite(audio.duration) ? audio.duration : totalDuration;
          setCurrent(audio.currentTime);
          if (dur > 0) setProgress(audio.currentTime / dur);
        }}
      />
    </div>
  );
}
